/** Identificador único para las filas */
export type TTableIdType = string | number;

/**
 * Representa un nodo en la estructura de datos de la tabla,
 * con información sobre su estado de selección, visualización y relaciones jerárquicas.
 * Cada nodo corresponde a una fila en la tabla y puede tener hijos (subfilas) dependiendo de la configuración de niveles.
 */
export class TableNode<TData = any, TDataChild = any> {

    readonly id: TTableIdType;
    readonly data: TData;
    readonly levelRow: number;
    readonly initialPos: number;

    // Estado de selección
    /** Indica si el nodo está seleccionado */
    private _isChecked: boolean = false;
    /** Indica si el nodo tiene selección indeterminada (algunos hijos seleccionados) */
    isIndeterminateChecked: boolean = false;

    // Estado de visualización
    isRowEnable: boolean = true;
    isCollapsed: boolean = true;

    // Relaciones jerárquicas
    itemParent: TableNode<any> | null = null;
    childDataOriginMap: Map<TTableIdType, TableNode<TDataChild>> = new Map();
    childDataFilteredArray: Array<TableNode<TDataChild>> = [];

    constructor(init: Partial<TableNode<TData, TDataChild>> & { id: TTableIdType; data: TData; levelRow: number; initialPos: number }) {
        this.id = init.id;
        this.data = init.data;
        this.levelRow = init.levelRow;
        this.initialPos = init.initialPos;
        Object.assign(this, init);
    }

    get isChecked(): boolean {
        return this._isChecked;
    }

    set isChecked(value: boolean) {
        if (value && !this.isRowEnable) return; // No se puede checkear un nodo deshabilitado
        this._isChecked = value;
    }

    /**
     * Un nodo tiene "selección activa" si está checkeado o si tiene hijos con selección activa (indeterminate)
     */
    get hasActiveSelection(): boolean {
        return this._isChecked || this.isIndeterminateChecked;
    }

    /**
     * Una fila es "hoja" cuando no tiene hijos en su nivel original (childDataOriginMap).
     */
    get isLeaf(): boolean {
        return this.childDataOriginMap.size === 0;
    }

    /** Lista de nodos hijos que tienen selección activa (están checkeados o indeterminados) */
    get listActiveSelectedChildNodes(): TableNode<TDataChild>[] {
        return this.childDataOriginMap
            ? Array.from(this.childDataOriginMap.values()).filter(child => child.hasActiveSelection)
            : [];
    }

    /**
     * Calcula y actualiza el estado de selección del nodo basándose en una lista de hijos filtrados (childDataFilteredArray).
     * @returns 'true' si el estado cambió, útil para saber si hay que propagar eventos.
     */
    public syncCheckStateWithChildren(): boolean {
        const children: Iterable<TableNode> = this.childDataFilteredArray;
        let enabledCount = 0;
        let checkedCount = 0;
        let indeterminateCount = 0;

        for (const child of children) {
            if (child.isRowEnable) { // NOTE solo considerar hijos habilitados
                enabledCount++;
                if (child._isChecked) checkedCount++;
                if (child.isIndeterminateChecked) indeterminateCount++;
            }
        }

        if (enabledCount === 0) { // No hay hijos habilidados o es una hoja, no puede ser indeterminate
            if (this.isIndeterminateChecked) {
                this.isIndeterminateChecked = false;
                return true;
            }
            return false;
        }

        const allChecked = checkedCount === enabledCount;
        const someChecked = !allChecked && (checkedCount > 0 || indeterminateCount > 0);

        const hasChanged = this._isChecked !== allChecked || this.isIndeterminateChecked !== someChecked;

        this._isChecked = allChecked;
        this.isIndeterminateChecked = someChecked;

        return hasChanged;
    }
}


/** Tipo TableNode con todas las propiedades de solo lectura */
export type ReadonlyTableNode<TData = any, TDataChild = any> = Readonly<TableNode<TData, TDataChild>>;